import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    BarChart3, Clock, Plane, Users, Loader2, Download, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getLocalToday, getLocalMonthStart } from '../utils/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function exportCSV(data, headers, filename) {
    const csv = [headers.join(','), ...data.map(row => headers.map(h => `"${row[h] ?? ''}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
}

export default function ReportsPage() {
    const { token, user } = useAuth();
    const authHeaders = { Authorization: `Bearer ${token}` };
    // Bug fix: filtrar pestañas según el rol del usuario. Cada endpoint tiene su propio
    // gate en el backend (reports.py). Ocultar tabs no permitidas evita el toast rojo
    // "Error al generar reporte" al abrir la pantalla con un rol restringido.
    const role = user?.role;
    const tabPerms = {
        punctuality: ['admin', 'ccod', 'gerencia', 'analista'],
        fleet: ['admin', 'ccod', 'gerencia', 'analista', 'maintenance'],
        crew: ['admin', 'crew_admin', 'ccod', 'gerencia'],
    };
    const allowedTabs = Object.entries(tabPerms).filter(([, roles]) => roles.includes(role)).map(([k]) => k);
    const defaultTab = allowedTabs[0] || 'fleet';
    const [activeTab, setActiveTab] = useState(defaultTab);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(getLocalMonthStart());
    const [endDate, setEndDate] = useState(getLocalToday());
    const [punctuality, setPunctuality] = useState(null);
    const [fleet, setFleet] = useState(null);
    const [crewHours, setCrewHours] = useState(null);
    const [crewList, setCrewList] = useState([]);
    const [selectedCrewId, setSelectedCrewId] = useState('all');

    useEffect(() => {
        // Solo hacer fetch si el usuario tiene al menos una pestaña permitida
        if (allowedTabs.length === 0) return;
        if (allowedTabs.includes('crew')) fetchCrewList();
        fetchReport(defaultTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCrewList = async () => {
        try {
            const res = await axios.get(`${API}/crew`, { headers: authHeaders });
            setCrewList(res.data);
        } catch { /* ignore */ }
    };

    const fetchReport = useCallback(async (type, crewId) => {
        setLoading(true);
        try {
            const crewParam = crewId && crewId !== 'all' ? `&crew_id=${crewId}` : '';
            const endpoints = {
                punctuality: `${API}/reports/punctuality?start_date=${startDate}&end_date=${endDate}`,
                fleet: `${API}/reports/fleet-utilization?start_date=${startDate}&end_date=${endDate}`,
                crew: `${API}/reports/crew-hours?start_date=${startDate}&end_date=${endDate}${crewParam}`,
            };
            const res = await axios.get(endpoints[type], { headers: authHeaders });
            if (type === 'punctuality') setPunctuality(res.data);
            if (type === 'fleet') setFleet(res.data);
            if (type === 'crew') setCrewHours(res.data);
        } catch (e) {
            // 403 = rol sin permiso para este reporte; ya escondimos la tab, pero por si
            // se navega manualmente. No mostrar el toast rojo generico en ese caso.
            if (e?.response?.status !== 403) {
                toast.error('Error al generar reporte');
            }
        }
        finally { setLoading(false); }
    }, [startDate, endDate]);

    const handleTabChange = (v) => {
        setActiveTab(v);
        fetchReport(v, v === 'crew' ? selectedCrewId : undefined);
    };

    const handleCrewFilter = (crewId) => {
        setSelectedCrewId(crewId);
        fetchReport('crew', crewId);
    };

    return (
        <div className="space-y-6" data-testid="reports-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="reports-title">Reportes</h1>
                    <p className="text-muted-foreground mt-1">Analisis operacional con exportacion</p>
                </div>
                <div className="flex items-center gap-2">
                    <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-[145px] input-aviation font-mono" data-testid="report-start" />
                    <span className="text-muted-foreground">—</span>
                    <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-[145px] input-aviation font-mono" data-testid="report-end" />
                </div>
            </div>

            {allowedTabs.length === 0 && (
                <Card className="card-aviation" data-testid="reports-no-access">
                    <CardContent className="p-8 text-center text-muted-foreground">
                        <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-50" />
                        <p className="font-bold">Tu rol no tiene acceso a los reportes disponibles.</p>
                        <p className="text-sm mt-1">Contacta a tu administrador si necesitas visibilidad.</p>
                    </CardContent>
                </Card>
            )}

            {allowedTabs.length > 0 && (
            <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList>
                    {allowedTabs.includes('punctuality') && <TabsTrigger value="punctuality" data-testid="tab-punctuality"><Clock className="w-3.5 h-3.5 mr-1" />Puntualidad</TabsTrigger>}
                    {allowedTabs.includes('fleet') && <TabsTrigger value="fleet" data-testid="tab-fleet"><Plane className="w-3.5 h-3.5 mr-1" />Flota</TabsTrigger>}
                    {allowedTabs.includes('crew') && <TabsTrigger value="crew" data-testid="tab-crew"><Users className="w-3.5 h-3.5 mr-1" />Tripulacion</TabsTrigger>}
                </TabsList>

                {/* Punctuality */}
                <TabsContent value="punctuality" className="space-y-4 mt-4">
                    <Button onClick={() => fetchReport('punctuality')} variant="outline" data-testid="generate-punctuality"><RefreshCw className="w-4 h-4 mr-2" />Actualizar</Button>
                    {loading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}
                    {punctuality && !loading && (
                        <div className="space-y-4">
                            {(punctuality.insufficient_data || punctuality.no_data > 0) && (
                                <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-400" data-testid="punctuality-data-warning">
                                    {punctuality.with_data === 0
                                        ? 'No hay vuelos con tiempos reales registrados en este periodo: no se puede calcular la puntualidad.'
                                        : punctuality.insufficient_data
                                            ? `Pocos datos: solo ${punctuality.with_data} vuelo(s) tienen tiempos reales. El porcentaje es orientativo.`
                                            : `${punctuality.no_data} vuelo(s) sin tiempos reales no se cuentan en el porcentaje.`}
                                </div>
                            )}
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                                <Card className="card-aviation"><CardContent className="p-3 text-center"><p className="text-2xl font-bold font-mono" data-testid="punctuality-total">{punctuality.total_flights}</p><p className="text-[10px] text-muted-foreground uppercase">Vuelos</p></CardContent></Card>
                                <Card className="card-aviation"><CardContent className="p-3 text-center"><p className="text-2xl font-bold font-mono text-emerald-500" data-testid="punctuality-pct">{punctuality.on_time_pct == null ? 'Sin datos' : `${punctuality.on_time_pct}%`}</p><p className="text-[10px] text-muted-foreground uppercase">A Tiempo</p></CardContent></Card>
                                <Card className="card-aviation"><CardContent className="p-3 text-center"><p className="text-2xl font-bold font-mono text-emerald-500" data-testid="punctuality-on-time">{punctuality.on_time}</p><p className="text-[10px] text-muted-foreground uppercase">Puntuales</p></CardContent></Card>
                                <Card className="card-aviation"><CardContent className="p-3 text-center"><p className="text-2xl font-bold font-mono text-amber-500" data-testid="punctuality-delayed">{punctuality.delayed}</p><p className="text-[10px] text-muted-foreground uppercase">Demorados</p></CardContent></Card>
                                <Card className="card-aviation"><CardContent className="p-3 text-center"><p className="text-2xl font-bold font-mono text-muted-foreground" data-testid="punctuality-no-data">{punctuality.no_data ?? 0}</p><p className="text-[10px] text-muted-foreground uppercase">Sin datos</p></CardContent></Card>
                            </div>
                            {punctuality.on_time_pct != null && (
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm"><span>Puntualidad ({punctuality.with_data} vuelos con datos)</span><span className="font-mono font-bold">{punctuality.on_time_pct}%</span></div>
                                    <Progress value={punctuality.on_time_pct} className="h-3" />
                                </div>
                            )}
                            {punctuality.delayed > 0 && <p className="text-sm text-muted-foreground">Demora promedio: <span className="font-mono font-bold">{punctuality.avg_delay_mins} min</span></p>}
                        </div>
                    )}
                    {!punctuality && !loading && (
                        <div className="text-center py-12 text-muted-foreground">
                            <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-40" />
                            <p>Cargando reporte...</p>
                        </div>
                    )}
                </TabsContent>

                {/* Fleet Utilization */}
                <TabsContent value="fleet" className="space-y-4 mt-4">
                    <div className="flex items-center gap-2">
                        <Button onClick={() => fetchReport('fleet')} variant="outline" data-testid="generate-fleet"><RefreshCw className="w-4 h-4 mr-2" />Actualizar</Button>
                        {fleet && <Button variant="outline" onClick={() => exportCSV(fleet.aircraft, ['registration', 'aircraft_type', 'cycles', 'block_hours', 'flight_hours'], 'fleet_utilization.csv')} data-testid="export-fleet"><Download className="w-4 h-4 mr-2" />CSV</Button>}
                    </div>
                    {loading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}
                    {fleet && !loading && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-3 gap-3">
                                <Card className="card-aviation"><CardContent className="p-3 text-center"><p className="text-2xl font-bold font-mono" data-testid="fleet-cycles">{fleet.totals.total_cycles}</p><p className="text-[10px] text-muted-foreground uppercase">Ciclos</p></CardContent></Card>
                                <Card className="card-aviation"><CardContent className="p-3 text-center"><p className="text-2xl font-bold font-mono" data-testid="fleet-block">{fleet.totals.total_block_hours}h</p><p className="text-[10px] text-muted-foreground uppercase">Horas Block</p></CardContent></Card>
                                <Card className="card-aviation"><CardContent className="p-3 text-center"><p className="text-2xl font-bold font-mono" data-testid="fleet-flight">{fleet.totals.total_flight_hours}h</p><p className="text-[10px] text-muted-foreground uppercase">Horas Vuelo</p></CardContent></Card>
                            </div>
                            <Card className="card-aviation"><CardContent className="p-0 overflow-x-auto">
                                <table className="table-aviation w-full">
                                    <thead><tr><th>Registro</th><th>Tipo</th><th>Estado</th><th>Ciclos</th><th>H. Block</th><th>H. Vuelo</th></tr></thead>
                                    <tbody>{fleet.aircraft.map(ac => (
                                        <tr key={ac.aircraft_id} data-testid={`fleet-row-${ac.registration}`}>
                                            <td className="font-mono font-bold text-primary">{ac.registration}</td>
                                            <td>{ac.aircraft_type}</td>
                                            <td><Badge variant="outline" className="text-[10px]">{ac.operational_status}</Badge></td>
                                            <td className="font-mono">{ac.cycles}</td>
                                            <td className="font-mono">{ac.block_hours}</td>
                                            <td className="font-mono">{ac.flight_hours}</td>
                                        </tr>
                                    ))}</tbody>
                                </table>
                            </CardContent></Card>
                        </div>
                    )}
                    {!fleet && !loading && (
                        <div className="text-center py-12 text-muted-foreground">
                            <Plane className="w-12 h-12 mx-auto mb-3 opacity-40" />
                            <p>Haz clic en "Actualizar" para generar</p>
                        </div>
                    )}
                </TabsContent>

                {/* Crew Hours */}
                <TabsContent value="crew" className="space-y-4 mt-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                        <Button onClick={() => fetchReport('crew', selectedCrewId)} variant="outline" data-testid="generate-crew"><RefreshCw className="w-4 h-4 mr-2" />Actualizar</Button>
                        <Select value={selectedCrewId} onValueChange={handleCrewFilter}>
                            <SelectTrigger className="w-[220px]" data-testid="crew-filter-select">
                                <SelectValue placeholder="Filtrar tripulante" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los tripulantes</SelectItem>
                                {crewList.map(c => (
                                    <SelectItem key={c.id} value={c.id}>{c.name || `${c.first_name || ''} ${c.last_name || ''}`}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {crewHours && <Button variant="outline" onClick={() => exportCSV(crewHours.crew, ['name', 'position', 'flights', 'flight_hours', 'block_hours', 'landings'], 'crew_hours.csv')} data-testid="export-crew"><Download className="w-4 h-4 mr-2" />CSV</Button>}
                    </div>
                    {loading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}
                    {crewHours && !loading && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-3 gap-3">
                                <Card className="card-aviation"><CardContent className="p-3 text-center"><p className="text-2xl font-bold font-mono" data-testid="crew-total-flights">{crewHours.totals.total_flights}</p><p className="text-[10px] text-muted-foreground uppercase">Vuelos</p></CardContent></Card>
                                <Card className="card-aviation"><CardContent className="p-3 text-center"><p className="text-2xl font-bold font-mono" data-testid="crew-total-flight-hours">{crewHours.totals.total_flight_hours}h</p><p className="text-[10px] text-muted-foreground uppercase">H. Vuelo Total</p></CardContent></Card>
                                <Card className="card-aviation"><CardContent className="p-3 text-center"><p className="text-2xl font-bold font-mono" data-testid="crew-total-block-hours">{crewHours.totals.total_block_hours}h</p><p className="text-[10px] text-muted-foreground uppercase">H. Block Total</p></CardContent></Card>
                            </div>
                            <Card className="card-aviation"><CardContent className="p-0 overflow-x-auto">
                                <table className="table-aviation w-full">
                                    <thead><tr><th>Nombre</th><th>Posicion</th><th>Vuelos</th><th>H. Vuelo</th><th>H. Block</th><th>LDG</th></tr></thead>
                                    <tbody>{crewHours.crew.length === 0 ? (
                                        <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">No hay datos para el periodo seleccionado</td></tr>
                                    ) : crewHours.crew.map(c => (
                                        <tr key={c.crew_id} data-testid={`crew-row-${c.crew_id}`}>
                                            <td className="font-medium">{c.name}</td>
                                            <td><Badge variant="outline" className="text-[10px]">{c.position}</Badge></td>
                                            <td className="font-mono">{c.flights}</td>
                                            <td className="font-mono">{c.flight_hours}</td>
                                            <td className="font-mono">{c.block_hours}</td>
                                            <td className="font-mono">{c.landings}</td>
                                        </tr>
                                    ))}</tbody>
                                </table>
                            </CardContent></Card>
                        </div>
                    )}
                    {!crewHours && !loading && (
                        <div className="text-center py-12 text-muted-foreground">
                            <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
                            <p>Haz clic en "Actualizar" para generar</p>
                        </div>
                    )}
                </TabsContent>
            </Tabs>
            )}
        </div>
    );
}
